import type CarrierAccount from '~/types/CarrierAccount';
import type CarrierAccountRegStatus from '~/types/CarrierAccountRegStatus';

import { type CarrierModalStatus } from '~/reducers/carrier-accounts/carrier-accounts.reducer';
import { type CarrierServiceLevelsEditPayload } from '~/types/CarrierAccount';

import { ActionKeys } from './constants';

interface CarrierAccountsErrorPayload {
  error: string;
}
export interface SaveCarrierAccountPayload {
  carrierAccount: CarrierAccount;
}

export interface EditCarrierAccountPayload {
  carrierAccount: CarrierAccount;
  id: string;
}

export interface EditCarrierAccountServiceLevelsPayload {
  carrierAccount: CarrierAccount;
  id: string;
  serviceLevelsPayload?: CarrierServiceLevelsEditPayload;
}

export interface FetchCarrierAccountPayload {
  master?: Maybe<boolean>;
  results?: number;
  unsorted?: Maybe<boolean>;
  user?: Maybe<boolean>;
}

export interface FetchCarrierAccountsSuccessPayload {
  carrierAccounts: CarrierAccount[];
}

interface FetchCarrierAccountRegStatusSuccessPayload {
  carrierAccountRegStatuses: CarrierAccountRegStatus[];
}

// Clear State
export const carrierAccountsClearState = () =>
  <const>{
    type: ActionKeys.CARRIER_ACCOUNTS_CLEAR_STATE,
  };

export const carrierAccountsClearSubmit = () =>
  <const>{
    type: ActionKeys.CARRIER_ACCOUNTS_CLEAR_SUBMIT,
  };

// Fetch Carrier Accounts
export const fetchCarrierAccounts = (payload?: FetchCarrierAccountPayload) =>
  <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNTS,
  };

export const fetchCarrierAccountsSuccess = (payload: FetchCarrierAccountsSuccessPayload) =>
  <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNTS_SUCCESS,
  };

export const fetchCarrierAccountsError = (payload: CarrierAccountsErrorPayload) =>
  <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNTS_ERROR,
  };

// Fetch Carrier Account Registration Status
export const fetchCarrierAccountRegStatuses = () =>
  <const>{
    type: ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES,
  };

export const fetchCarrierAccountRegStatusesSuccess = (
  payload: FetchCarrierAccountRegStatusSuccessPayload,
) =>
  <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES_SUCCESS,
  };

export const fetchCarrierAccountRegStatusesError = (payload: CarrierAccountsErrorPayload) =>
  <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES_ERROR,
  };

// Connect
export const connectCarrierAccount = (payload: SaveCarrierAccountPayload) =>
  <const>{
    payload,
    type: ActionKeys.CONNECT_CARRIER_ACCOUNT,
  };

export const connectCarrierAccountSuccess = (
  payload: Omit<SaveCarrierAccountPayload, 'trackingReference'>,
) =>
  <const>{
    payload,
    type: ActionKeys.CONNECT_CARRIER_ACCOUNT_SUCCESS,
  };

export const connectCarrierAccountError = (payload: CarrierAccountsErrorPayload) =>
  <const>{
    payload,
    type: ActionKeys.CONNECT_CARRIER_ACCOUNT_ERROR,
  };

// Edit
export const editCarrierAccount = (payload: EditCarrierAccountPayload) =>
  <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT,
  };

export const editCarrierAccountSuccess = (
  payload: Omit<SaveCarrierAccountPayload, 'trackingReference'>,
) =>
  <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_SUCCESS,
  };

export const editCarrierAccountError = (payload: CarrierAccountsErrorPayload) =>
  <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_ERROR,
  };

// Edit Carrier Service Levels
export const editCarrierAccountServiceLevels = (payload: EditCarrierAccountServiceLevelsPayload) =>
  <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS,
  };

export const editCarrierAccountServiceLevelsSuccess = (payload) =>
  <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS_SUCCESS,
  };

export const editCarrierAccountServiceLevelsError = (payload) =>
  <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS_ERROR,
  };

// Create
export const createCarrierAccount = (payload: SaveCarrierAccountPayload) =>
  <const>{
    payload,
    type: ActionKeys.CREATE_CARRIER_ACCOUNT,
  };

export const createCarrierAccountSuccess = (payload: SaveCarrierAccountPayload) =>
  <const>{
    payload,
    type: ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS,
  };

export const createCarrierAccountError = (payload: CarrierAccountsErrorPayload) =>
  <const>{
    payload,
    type: ActionKeys.CREATE_CARRIER_ACCOUNT_ERROR,
  };

// Modal
export const toggleCarrierModal = (modalStatus: CarrierModalStatus) =>
  <const>{
    payload: {
      modalStatus,
    },
    type: ActionKeys.TOGGLE_CARRIER_MODAL,
  };

export type CarrierAccountsActions =
  | ReturnType<typeof carrierAccountsClearState>
  | ReturnType<typeof carrierAccountsClearSubmit>
  | ReturnType<typeof connectCarrierAccount>
  | ReturnType<typeof connectCarrierAccountError>
  | ReturnType<typeof connectCarrierAccountSuccess>
  | ReturnType<typeof createCarrierAccount>
  | ReturnType<typeof createCarrierAccountError>
  | ReturnType<typeof createCarrierAccountSuccess>
  | ReturnType<typeof editCarrierAccount>
  | ReturnType<typeof editCarrierAccountError>
  | ReturnType<typeof editCarrierAccountServiceLevels>
  | ReturnType<typeof editCarrierAccountServiceLevelsError>
  | ReturnType<typeof editCarrierAccountServiceLevelsSuccess>
  | ReturnType<typeof editCarrierAccountSuccess>
  | ReturnType<typeof fetchCarrierAccountRegStatuses>
  | ReturnType<typeof fetchCarrierAccountRegStatusesError>
  | ReturnType<typeof fetchCarrierAccountRegStatusesSuccess>
  | ReturnType<typeof fetchCarrierAccounts>
  | ReturnType<typeof fetchCarrierAccountsError>
  | ReturnType<typeof fetchCarrierAccountsSuccess>
  | ReturnType<typeof toggleCarrierModal>;
